import React, { useCallback, useEffect, useState } from 'react'
import { Trans } from 'react-i18next'

import { parseVariableTextActionOutput } from '@/core/app-builder/parser/variable-text'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { getDataSourceKeyForComponent } from '@/shared/builder/utils/components'

import RowContextInput from '../RowContextInput'
import { variableTextHasColumnValue } from '../util'
import { PanelProps } from './panel.type'

export default function PanelTemporaryVariable({ data, setValue }: PanelProps) {
  const { value, allActions } = data
  const { dbClient, language, userSpecific } = useDbClientContext()
  const { activeLayerClient } = useActiveLayerContext()

  const [rowIndex, setRowIndex] = useState('1')
  const [isSimulating, setIsSimulating] = useState(false)
  const [simulatedOutput, setSimulatedOutput] = useState<string | null>(null)

  const { activeComponent, activeLayerContainer } = useActiveLayerContext()

  const dataSourceKey = getDataSourceKeyForComponent(activeComponent?.compId || '')
  const tableId: any = dataSourceKey
    ? (activeLayerContainer?.elementSettings?.[activeComponent?.id as string]?.[dataSourceKey] ?? null)
    : null

  // Function to simulate the previous node and get its output
  const handleSimulate = useCallback(async () => {
    if (!dbClient || !activeLayerClient) return

    const rows = dbClient.tablesMap.get(tableId)?.GetDbWorkerRows(language, userSpecific)
    if (!rows) return

    const parsedRowIndex = parseInt(rowIndex) - 1 // Convert to 0-based index
    const rowId = parsedRowIndex >= 0 ? rows[parsedRowIndex]._id : undefined

    setIsSimulating(true)

    try {
      let result: string | null = null
      result = await parseVariableTextActionOutput(
        value || [],
        dbClient,
        activeLayerClient,
        'user@example.com', // placeholder userEmail
        language,
        userSpecific,
        tableId || '',
        rowId,
        allActions || []
      )

      setSimulatedOutput(result || 'No output')
      setValue?.({
        value: result,
      })
    } catch (error) {
      console.error('Error simulating previous node:', error)
    } finally {
      setIsSimulating(false)
    }
  }, [dbClient, activeLayerClient, rowIndex, value, language, userSpecific, tableId, allActions, setValue])

  // Auto-simulate when row index changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleSimulate()
    }, 500) // Debounce for 500ms

    return () => clearTimeout(timeoutId)
  }, [handleSimulate])

  const handleRowIndexChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    if (val === '-' || val === '') {
      setRowIndex(val)
    } else {
      const numVal = Number(val)
      if (!isNaN(numVal)) {
        setRowIndex(numVal.toString())
      }
    }
  }, [])

  const hasColumnData = variableTextHasColumnValue(value)
  const shouldShowRowContextInput = hasColumnData

  return (
    <div className='h-full flex flex-col bg-gray-50 dark:bg-gray-900'>
      <div className='flex-1 space-y-4'>
        <div className='space-y-4'>
          {/* Row Context Input */}
          {shouldShowRowContextInput && (
            <RowContextInput
              rowIndex={rowIndex}
              handleRowIndexChange={handleRowIndexChange}
              handleSimulate={handleSimulate}
              isLoading={isSimulating}
            />
          )}

          {/* Simulated Output */}
          <div>
            <div className='text-sm font-medium text-gray-900 dark:text-white mb-2'>
              <Trans>Output:</Trans>
            </div>
            <div className='bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-600 min-h-[60px]'>
              {simulatedOutput ? (
                <div className='space-y-2'>
                  <div className='text-sm text-gray-900 dark:text-white font-mono p-2 rounded border border-gray-200 dark:border-gray-600'>
                    {simulatedOutput || <span className='text-gray-500 italic'>Empty result</span>}
                  </div>
                  {shouldShowRowContextInput && (
                    <div className='text-xs text-gray-500 dark:text-gray-400'>
                      <Trans>Simulated with row index: {rowIndex}</Trans>
                    </div>
                  )}
                </div>
              ) : (
                <div className='text-sm text-gray-500 dark:text-gray-400 italic'>
                  <Trans>Click &quot;Simulate&quot; to see expected output</Trans>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
